/**
 * NPEAllocationListPage — committed NPE Allocations.
 * Access parity: form_frmPurchaseMainSubFormNPEAllocation + sub-form
 * frmPurchaseQryNPEAllocation. Cols/buttons/rules copy 1:1 từ Access;
 * UI giữ web layout (MUI table, filter bar).
 */
import { type MouseEvent, useMemo, useState } from 'react'
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Stack, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, Typography,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'
import AddIcon from '@mui/icons-material/Add'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import BrandState from '@/components/BrandState'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  DataframeBody,
  FilterBar,
  FilterButton,
  filterBarBtnSx,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
} from '@/components/dataList'
import ConfirmDialog from '@/components/ConfirmDialog'
import ResizeHandle from '@/components/ResizeHandle'
import { useColumnWidths } from '@/hooks/useColumnWidths'
import { HIDE_ON_MOBILE_INLINE, HIDE_ON_TABLET_INLINE, TABLE_PAGINATION_COMPACT } from '@/theme/responsive'
import { useSnackbar } from '@/hooks/useSnackbar'
import { usePagePermission } from '../../hooks/usePagePermission'
import {
  useDeleteNPEAllocationsByFixationMutation,
  useListNPEAllocationsQuery,
} from '@/store/api/purchaseContractApi'
import { API_BASE_URL } from '@/config/api'
import { useAppSelector } from '@/store'
import type { NPEAllocationOut } from '@/types/api'
import NPEAllocationAddDialog from './NPEAllocationAddDialog'
import { getErrorMessage } from '@/utils/error'
import { sumNumericColumn } from '@/utils/tableTotals'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '@/utils/tableSort'

import { FuturesColumnsMenu } from '../futures/FuturesColumnsMenu'
import { usePreferenceColumns } from '@/utils/datasheetColumns/usePreferenceColumns'

export interface NPEAllocationListPageProps {
  embeddedNpeContract?: string
}

type FilterKind = 'search'
const ALL_ROWS_PAGE_SIZE = 1_000_000

const actionToolbarBtnSx: SxProps<Theme> = { minWidth: 0, px: 0.75, py: 0.2, minHeight: 26, fontSize: '0.72rem', lineHeight: 1.1, textTransform: 'none' }

type ColumnKind = 'text' | 'date' | 'datetime' | 'number'
type ColumnDef = { key: Extract<keyof NPEAllocationOut, string>; label: string; kind?: ColumnKind; align?: 'right'; digits?: number; filter?: ColumnFilterKind; sticky?: boolean; plainInt?: boolean }
// Cột không sum: ID, ngày, giá đơn vị, mỗi-fixation-snapshot-không-cộng-dồn.
const NON_SUM_TOTAL_KEYS = new Set<string>([
  'allocation_id', 'fixation_price', 'fixation_quantity',
])

// Access form_frmPurchaseQryNPEAllocation cols (RecordSource = PurchaseQryNPE_Allocation
// → public_v_npe_allocation). Order/labels theo spec chị Đẹp gửi.
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'allocation_id', label: 'Allocation ID', kind: 'number', align: 'right', digits: 0, plainInt: true },
  { key: 'allocation_date', label: 'Allocation Date', kind: 'date' },
  { key: 'contract_no', label: 'NPEs Contract', filter: 'checkboxes', sticky: true },
  { key: 'fixation_no', label: 'Fixation No', filter: 'checkboxes' },
  { key: 'advanced_no', label: 'Advanced No', filter: 'checkboxes' },
  { key: 'allocation_money', label: 'Allocation Paid (VND)', kind: 'number', align: 'right', digits: 0 },
  { key: 'allocation_interest', label: 'Allocation Interest (VND)', kind: 'number', align: 'right', digits: 0 },
  { key: 'fixation_date', label: 'Fixation Date', kind: 'date' },
  { key: 'fixation_quantity', label: 'Fixation Quantity', kind: 'number', align: 'right', digits: 0 },
  { key: 'fixation_price', label: 'Fixation Price', kind: 'number', align: 'right', digits: 2 },
  { key: 'note', label: 'Allocation Note' },
  { key: 'date_updated', label: 'Updated Date', kind: 'datetime' },
  { key: 'person_updated', label: 'Updated Person', filter: 'checkboxes' },
]

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const formatAccessDate = (v: unknown): string => { if (!v) return ''; const d = new Date(String(v)); if (Number.isNaN(d.getTime())) return String(v); return `${`${d.getDate()}`.padStart(2, '0')}-${ACCESS_MONTHS[d.getMonth()]}-${`${d.getFullYear()}`}` }
const formatAccessDateTime = (v: unknown): string => {
  if (!v) return ''
  const d = new Date(String(v))
  if (Number.isNaN(d.getTime())) return String(v)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${month}-${year} ${hh}:${mm}`
}
const formatNumberFixed = (v: number, digits = 0): string => digits > 0 ? v.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits }) : (Number.isInteger(v) ? v.toLocaleString('en-US') : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))

const cellValue = (row: NPEAllocationOut, col: ColumnDef): string => {
  const val = row[col.key]
  if (val == null) return ''
  if (col.kind === 'date') return formatAccessDate(val)
  if (col.kind === 'datetime') return formatAccessDateTime(val)
  if (col.plainInt) { const n = typeof val === 'number' ? val : Number(val); return Number.isFinite(n) ? String(Math.trunc(n)) : String(val) }
  if (col.kind === 'number' && typeof val === 'number') return formatNumberFixed(val, col.digits ?? 0)
  if (typeof val === 'number') return formatNumberFixed(val, 0)
  return String(val)
}

export function NPEAllocationListPage({ embeddedNpeContract }: NPEAllocationListPageProps = {}) {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()

  const { selectedId, setSelectedId, toggleSelect } = useRowSelection<number>()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(-1)

  const { getWidth, startResize } = useColumnWidths('purchase.npe-allocation')

  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<keyof NPEAllocationOut> | null>(null)

  const filterPopover = useFilterPopover<FilterKind>()
  const openFilterPopover = (kind: FilterKind, e: MouseEvent<HTMLElement>) => filterPopover.open(kind, e)
  const closeFilterPopover = () => filterPopover.close()

  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: keyof NPEAllocationOut
    position: ColumnFilterAnchorPosition
  } | null>(null)
  const closeColPopover = () => setColPopover(null)

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => { setSearchTerm(''); columnFilters.clearAll(); setPage(0); closeFilterPopover() }

  // Access btnDelete xóa bulk theo `fixation_no`; confirm hiển thị count
  // các dòng cùng fixation_no sẽ bị soft-delete cùng lúc.
  const [confirmDel, setConfirmDel] = useState<{ fixationNo: string; count: number } | null>(null)
  const [newOpen, setNewOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [exporting, setExporting] = useState(false)

  const { data, isLoading, isFetching, error } = useListNPEAllocationsQuery({ page: 1, page_size: ALL_ROWS_PAGE_SIZE }, { refetchOnMountOrArgChange: true })
  const [deleteByFixation, { isLoading: deleting }] = useDeleteNPEAllocationsByFixationMutation()
  // Access btnExport dùng email user — VBA: `& email=` & USER_EMAIL.
  const userEmail = useAppSelector((s) => s.auth.user?.email ?? '')

  const baseRows = useMemo(() => {
    const items = data?.items ?? []
    return embeddedNpeContract ? items.filter((it) => it.npe_contract === embeddedNpeContract) : items
  }, [data, embeddedNpeContract])

  const columnsCtrl = usePreferenceColumns<ColumnDef>({
    scope: 'purchase',
    prefKey: 'columns',
    subKey: 'npe-allocation',
    allColumns: TABLE_COLUMNS,
    widthsTableKey: 'purchase.npe-allocation',
  })
  const { visibleColumns, openMenu } = columnsCtrl

  const minTableWidth = useMemo(
    () => Math.max(1200, visibleColumns.length * 140),
    [visibleColumns.length],
  )

  const filteredRows = useMemo(() =>
    baseRows.filter((row) => {
      if (!rowMatchesSearch(row as unknown as Record<string, unknown>, searchTerm, TABLE_COLUMNS as unknown as { key: string }[])) return false
      if (!rowMatchesAllColumnFilters(row as unknown as Record<string, unknown>, columnFilters.filters)) return false
      return true
    }), [baseRows, searchTerm, columnFilters.filters])

  // Per-column ▽ popover metadata
  const popoverColumn = useMemo<ColumnDef | null>(
    () => (colPopover ? TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover],
  )
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows as never) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(baseRows as unknown as Record<string, unknown>[], String(colPopover.key))
        : undefined,
    [popoverKind, colPopover, baseRows],
  )

  const sortedRows = useMemo(() => sortTableRows(filteredRows, sortState, visibleColumns), [filteredRows, sortState, visibleColumns])
  const rows = useMemo(() => rowsPerPage === -1 ? sortedRows : sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [page, rowsPerPage, sortedRows])

  const totalRowValues = useMemo(
    () =>
      new Map(
        visibleColumns.filter((col) => col.kind === 'number' && !col.plainInt && !NON_SUM_TOTAL_KEYS.has(String(col.key))).map(
          (col) => [String(col.key), sumNumericColumn(filteredRows, col.key)],
        ),
      ),
    [filteredRows, visibleColumns],
  )

  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`
  const selectedRow = useMemo(() => baseRows.find((r) => r.allocation_id === selectedId) ?? null, [baseRows, selectedId])
  const toggleSort = (key: keyof NPEAllocationOut) => { setSortState((current) => toggleTableSort(current, key)); setPage(0) }

  // Access btnDelete: confirm với count "Do you want to delete N Allocated
  // Payment Advanced = '<fixation_no>' ?". Count = số rows is_status=0 cùng
  // fixation_no — derive từ baseRows (đã filter is_status=0).
  const openDeleteConfirm = () => {
    if (!selectedRow?.fixation_no) return
    const count = baseRows.filter((r) => r.fixation_no === selectedRow.fixation_no).length
    setConfirmDel({ fixationNo: selectedRow.fixation_no, count })
  }
  const handleConfirmDel = async () => {
    if (!confirmDel) return
    try {
      await deleteByFixation(confirmDel.fixationNo).unwrap()
      snack.success(`Successfully Deleted ${confirmDel.count} allocation(s)`) ; setConfirmDel(null); setSelectedId(null)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to delete allocations'))
    }
  }

  // Access btnExport_Click (form_frmPurchaseMainSubFormNPEAllocation):
  //   apiUrl & "/download/fixation/excel?token=JDHK-NDEB-DBCK-DGEJ&email="
  //         & USER_EMAIL & "&fixation_no=" & fixation_no
  // FE call legacy proxy `/erp-legacy/download/fixation/excel` —
  // forward sang `api_erp_prod.download_fixation_excel` (giữ template
  // `template_FixationExcel.xlsx` + token check + filename Content-Disposition).
  const handleExport = async () => {
    if (!selectedRow?.fixation_no) {
      snack.warning('Please choose a record!')
      return
    }
    // Access btnExport validate `DCount > 0` — count rows cùng fixation_no
    // is_status=0. Web tương đương: kiểm baseRows.
    const allocCount = baseRows.filter((r) => r.fixation_no === selectedRow.fixation_no).length
    if (allocCount <= 0) {
      snack.warning('This Fixation Number does not have allocated lines!')
      return
    }
    if (!userEmail) {
      snack.error('Missing user email')
      return
    }
    setExporting(true)
    try {
      // Token + URL pattern y hệt Access VBA (token hardcoded `JDHK-NDEB-DBCK-DGEJ`,
      // email từ user session).
      const url = `${API_BASE_URL}/erp-legacy/download/fixation/excel?token=${encodeURIComponent('JDHK-NDEB-DBCK-DGEJ')}&email=${encodeURIComponent(userEmail)}&fixation_no=${encodeURIComponent(selectedRow.fixation_no)}`
      const resp = await fetch(url)
      if (!resp.ok) throw new Error('Download fail!')
      const blob = await resp.blob()
      const disposition = resp.headers.get('content-disposition') ?? ''
      const star = /filename\*\s*=\s*([^;]+)/i.exec(disposition)
      const plain = /filename\s*=\s*([^;]+)/i.exec(disposition)
      let filename = ''
      if (star?.[1]) {
        const v = star[1].trim().replace(/^UTF-8''/i, '').replace(/^"|"$/g, '')
        try { filename = decodeURIComponent(v) } catch { filename = v }
      } else if (plain?.[1]) {
        filename = plain[1].trim().replace(/^"|"$/g, '')
      }
      const href = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = href
      a.download = filename || `ChotGia_${selectedRow.fixation_no.replace(/\//g, '_')}.xlsx`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(href)
      snack.success(`Downloaded ${a.download}`)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Download fail!'))
    } finally {
      setExporting(false)
    }
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        // Bảng FILL hết không gian flex còn lại thay vì clamp height theo viewport
        // → bỏ khoảng trống dưới bảng. Override clamp của `.app-dataframe`.
        '& > .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' },
      }}
    >
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        rightSlot={
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
            <Button variant="contained" size="small" color="success" sx={actionToolbarBtnSx} disabled={!canEdit} onClick={() => setNewOpen(true)} startIcon={<AddIcon sx={{ fontSize: 16 }} />} title="New"><Box component="span" sx={HIDE_ON_MOBILE_INLINE}>New</Box></Button>
            <Button variant="outlined" size="small" color="primary" sx={actionToolbarBtnSx} disabled={!selectedRow || !canEdit} onClick={() => setEditOpen(true)} startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 16 }} />} title="View"><Box component="span" sx={HIDE_ON_MOBILE_INLINE}>View</Box></Button>
            <Button variant="outlined" size="small" color="error" sx={actionToolbarBtnSx} disabled={!selectedRow || !canEdit} onClick={openDeleteConfirm} startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />} title="Delete"><Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Delete</Box></Button>
            <Button variant="outlined" size="small" color="info" sx={actionToolbarBtnSx} disabled={!selectedRow || exporting || !canEdit} onClick={() => void handleExport()} startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}>{exporting ? 'Exporting…' : 'Export'}</Button>
          </Stack>
        }
      >
        <FilterButton label="Search" active={!!searchTerm.trim()} onClick={(e) => openFilterPopover('search', e)} />
        <Button size="small" variant="outlined" endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />} onClick={(e) => openMenu(e.currentTarget)} sx={filterBarBtnSx} title="Columns">
          <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
          <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
        </Button>
      </FilterBar>
      <Divider sx={{ mb: 0.5 }} />

      <Box className="app-dataframe">
        <Box className="app-dataframe-toolbar" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, flexWrap: 'wrap', minHeight: 24, py: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>Total: {filteredRows.length}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>Loaded: {baseRows.length}</Typography>
            <TablePagination component="div" count={filteredRows.length} page={rowsPerPage === -1 ? 0 : page} onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0) }} rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]} labelRowsPerPage="Rows" sx={TABLE_PAGINATION_COMPACT} />
          </Stack>
        </Box>

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={closeFilterPopover}
          maxWidth={360}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0) }}
              placeholder="Fixation no, advance, NPE..."
              label="Search NPE Allocations"
              onClear={closeFilterPopover}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={closeColPopover}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
          onChange={(v) => { if (!colPopover) return; columnFilters.setFilter(String(colPopover.key), v); setPage(0) }}
          options={popoverOptions}
        />

        <FuturesColumnsMenu controller={columnsCtrl} />

        <DataframeBody loading={isLoading} fetching={isFetching}>
          {error ? (
            <BrandState
              tone="error"
              title="Could not load NPE Allocations"
              description="Please refresh the page or contact the system administrator if the issue continues."
            />
          ) : isLoading && baseRows.length === 0 ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState
              title="No NPE Allocations found"
              description="Try adjusting the table filters above."
            />
          ) : (
          <TableVirtuoso
            key={gridKey}
            style={{ height: '100%' }}
            data={rows}
            fixedHeaderContent={() => (
              <TableRow>
                {visibleColumns.map((col) => {
                  const w = getWidth(String(col.key))
                  return (
                    <TableCell
                      key={String(col.key)}
                      align={col.align}
                      className={`app-table-header-cell${col.sticky ? ' app-sticky-col' : ''}`}
                      onClick={() => toggleSort(col.key)}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      // userSelect:'text' để bôi đen + copy tên cột (click-sort vẫn chạy).
                      sx={{ whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'text', position: 'relative' }}
                    >
                      {col.label}
                      {getTableSortSuffix(sortState, col.key)}
                      <ColumnFilterIcon
                        active={columnFilters.isActive(String(col.key))}
                        onClick={(position) => setColPopover({ key: col.key, position })}
                        ariaLabel={`Filter ${col.label}`}
                      />
                      <ResizeHandle onMouseDown={(e) => startResize(String(col.key), e)} />
                    </TableCell>
                  )
                })}
              </TableRow>
            )}
            fixedFooterContent={() => (
              <TableRow>
                {visibleColumns.map((col, index) => {
                  const total = totalRowValues.get(String(col.key))
                  const val = total == null ? '' : formatNumberFixed(total, col.digits ?? 0)
                  return (
                    <TableCell
                      key={String(col.key)}
                      align={col.align}
                      className={col.sticky ? 'app-sticky-col' : undefined}
                      sx={{
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        py: 0.5,
                        bgcolor: 'background.paper',
                        borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      {index === 0 ? 'Total' : val}
                    </TableCell>
                  )
                })}
              </TableRow>
            )}
            itemContent={(_: number, row: NPEAllocationOut) =>
              visibleColumns.map((col) => {
                const val = cellValue(row, col)
                const w = getWidth(String(col.key))
                const wrap = getWidth(String(col.key)) != null
                if (wrap) {
                  return (
                    <TableCell
                      key={String(col.key)}
                      align={col.align}
                      className={col.sticky ? 'app-sticky-col' : undefined}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'top' }}
                    >
                      <Box component="span" className="app-table-cell-wrap">{val || '-'}</Box>
                    </TableCell>
                  )
                }
                return (
                  <TableCell
                    key={String(col.key)}
                    align={col.align}
                    className={col.sticky ? 'app-sticky-col' : undefined}
                    style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                    sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'top' }}
                  >
                    <Box component="span" title={val || '-'} sx={{ display: 'inline-block', width: '100%' }}>{val || '-'}</Box>
                  </TableCell>
                )
              })
            }
            components={{
              Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />,
              TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
              TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
              TableRow: (props: any) => {
                const idx = Number((props as any)['data-item-index'])
                const id = Number.isFinite(idx) ? rows[idx]?.allocation_id : null
                return <TableRow {...props} hover selected={id != null && id === selectedId} sx={{ cursor: 'pointer' }} onClick={() => { if (id != null) toggleSelect(id) }} />
              },
            }}
          />
          )}
        </DataframeBody>
      </Box>

      {/* Access btnNew_Click: mở `frmPurchaseQryNPEAllocation_Add` →
          single Dialog với 3 sections, Save/Cancel/Refresh. */}
      <NPEAllocationAddDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCommitted={() => setNewOpen(false)}
      />
      <NPEAllocationEditDialog
        open={editOpen && !!selectedRow}
        fixationNo={selectedRow?.fixation_no ?? ''}
        rowsForFixation={baseRows.filter((r) => selectedRow && r.fixation_no === selectedRow.fixation_no)}
        onClose={() => setEditOpen(false)}
      />
      {/* Access btnDelete confirm: "Do you want to delete N Allocated Payment
          Advanced = '<fixation_no>' ?" — count = số dòng cùng fixation_no. */}
      <ConfirmDialog
        open={!!confirmDel}
        title="Delete Allocated Payment Advanced"
        message={confirmDel ? `Do you want to delete ${confirmDel.count} Allocated Payment Advanced = '${confirmDel.fixationNo}' ?` : ''}
        destructive
        loading={deleting}
        onConfirm={() => void handleConfirmDel()}
        onCancel={() => setConfirmDel(null)}
      />
    </Box>
  )
}

/**
 * NPEAllocationEditDialog — view-only.
 * Access parity: form_frmPurchaseQryNPEAllocationView_Add (chỉ có btnCancel,
 * không có btnSave → view-only). Hiển thị header (fixation snapshot) + danh
 * sách allocation lines cùng fixation_no.
 */
function NPEAllocationEditDialog({
  open, fixationNo, rowsForFixation, onClose,
}: {
  open: boolean
  fixationNo: string
  rowsForFixation: NPEAllocationOut[]
  onClose: () => void
}) {
  const header = rowsForFixation[0] ?? null
  const totalQty = useMemo(
    () => rowsForFixation.reduce((s, r) => s + (Number(r.allocation_quantity) || 0), 0),
    [rowsForFixation],
  )
  const totalMoney = useMemo(
    () => rowsForFixation.reduce((s, r) => s + (Number(r.allocation_money) || 0), 0),
    [rowsForFixation],
  )
  const totalInterest = useMemo(
    () => rowsForFixation.reduce((s, r) => s + (Number(r.allocation_interest) || 0), 0),
    [rowsForFixation],
  )

  const renderField = (label: string, value: string) => (
    <Box sx={{ display: 'flex', gap: 1, py: 0.25 }}>
      <Typography variant="caption" sx={{ minWidth: 130, color: 'text.secondary' }}>{label}</Typography>
      <Typography variant="caption" sx={{ fontWeight: 600 }}>{value || '-'}</Typography>
    </Box>
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ py: 1 }}>
        <Box component="span">NPE Allocation — View</Box>
        <Typography variant="caption" sx={{ ml: 2, color: 'text.secondary' }}>
          Fixation: <b>{fixationNo}</b>
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {/* Access Detail section fields — read-only header. Field list theo
            form_frmPurchaseQryNPEAllocationView_Add: fixation_date, supplier
            (= cp name via consignment_p), contract_no, p_contract_no,
            fixation_no, price, quantity_kg, amount_after_fixed, allocated_amount,
            to_be_allocate, advanced_date, note. Web hiển thị các field FE
            đã có từ NPEAllocationOut. */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mb: 2 }}>
          {renderField('Fixation No', header?.fixation_no ?? fixationNo)}
          {renderField('Fixation Date', formatAccessDate(header?.fixation_date))}
          {renderField('Allocation Date', formatAccessDate(header?.allocation_date))}
          {renderField('Contract No', String(header?.contract_no ?? ''))}
          {renderField('P Contract', String(header?.p_contract_no ?? ''))}
          {renderField('Advance No', String(header?.advanced_no ?? ''))}
          {renderField('Fixation Price', header?.fixation_price != null ? formatNumberFixed(Number(header.fixation_price), 2) : '')}
          {renderField('Fixation Quantity', header?.fixation_quantity != null ? formatNumberFixed(Number(header.fixation_quantity), 0) : '')}
          {renderField('Amount after fixed', header?.fixation_price != null && header?.fixation_quantity != null ? formatNumberFixed(Number(header.fixation_price) * Number(header.fixation_quantity), 0) : '')}
        </Box>
        <Box sx={{ mb: 1 }}>{renderField('Note', String(header?.note ?? ''))}</Box>

        {/* Sub-form (Access subForm) — list các allocation lines cùng fixation_no. */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 0.5 }}>
          Allocations ({rowsForFixation.length})
        </Typography>
        <Box sx={{ maxHeight: 340, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Advance No</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Allocation Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Allocation Money</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Allocation Quantity</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Allocation Interest</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Note</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rowsForFixation.map((r) => (
                <TableRow key={r.allocation_id} hover>
                  <TableCell>{r.allocation_id}</TableCell>
                  <TableCell>{r.advanced_no ?? '-'}</TableCell>
                  <TableCell>{formatAccessDate(r.allocation_date)}</TableCell>
                  <TableCell align="right">{r.allocation_money != null ? formatNumberFixed(Number(r.allocation_money), 0) : '-'}</TableCell>
                  <TableCell align="right">{r.allocation_quantity != null ? formatNumberFixed(Number(r.allocation_quantity), 0) : '-'}</TableCell>
                  <TableCell align="right">{r.allocation_interest != null ? formatNumberFixed(Number(r.allocation_interest), 0) : '-'}</TableCell>
                  <TableCell>{r.note ?? '-'}</TableCell>
                </TableRow>
              ))}
              {rowsForFixation.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ color: 'text.secondary' }}>No allocations.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 1, fontSize: '0.75rem' }}>
          <span>Total Qty: <b>{formatNumberFixed(totalQty, 0)}</b></span>
          <span>Total Money: <b>{formatNumberFixed(totalMoney, 0)}</b></span>
          <span>Total Interest: <b>{formatNumberFixed(totalInterest, 0)}</b></span>
        </Box>
      </DialogContent>
      <DialogActions>
        {/* Access View_Add chỉ có btnCancel (no Save) → web tương đương Close. */}
        <Button onClick={onClose} size="small" variant="contained">Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default NPEAllocationListPage
